// ---------------------------------------------------------------------------
// Autoridade única de rolagem do app.
//
// Até 06/08/2026 quem rolava era sempre o documento, e `window.scrollY` /
// `window.scrollTo` eram a resposta certa em qualquer tela. O frame de desktop
// (`AppFrame`) quebra essa premissa: a partir do corte de largura o app passa a
// morar numa casca `position: fixed`, e quem rola vira um div interno. Quem
// continuar falando com a `window` lá não recebe erro nenhum — `scrollY` lê 0
// para sempre e `scrollTo` vira no-op. É a classe de falha silenciosa que este
// módulo existe para impedir: um lugar só sabe quem rola.
//
// Por que a decisão é lida do CSS (`overflowY` computado) e não de um
// `matchMedia` repetido aqui: a media query já vive no `AppFrame`. Duplicar o
// número do breakpoint em JS cria duas fontes de verdade que divergem no dia em
// que alguém mexe no CSS — e a divergência seria invisível, porque os dois
// caminhos "funcionam", só que um deles rola o elemento errado.
//
// No mobile WEB o div existe mas NÃO é scroller (`overflow: visible`), então
// `getAppScroller()` devolve `null` e tudo cai no comportamento de sempre —
// inclusive a auto-ocultação da barra de endereço do Safari/Chrome, que depende
// do documento rolar e que um scroller interno mataria.
//
// ⚠️ Exceção desde 28/09: no app de iOS o div É o scroller também no celular
// (`frameScrollMode.ts`, BUG-300 — o gesto de voltar precisa do documento
// parado). Nada aqui muda por isso: a decisão continua lida do CSS.
// ---------------------------------------------------------------------------

/** Id do div que rola dentro do frame. Único ponto que casa CSS e JS. */
export const APP_SCROLL_ROOT_ID = "app-scroll-root";

/**
 * O elemento que realmente rola, ou `null` quando quem rola é o documento.
 *
 * `null` não é caso de erro: é o caminho normal no mobile e em toda rota fora
 * do frame (landing, institucionais, login, /admin).
 */
export function getAppScroller(): HTMLElement | null {
  if (typeof document === "undefined") return null;

  const el = document.getElementById(APP_SCROLL_ROOT_ID);
  if (!el) return null;

  // `getComputedStyle` e não a classe: a classe é `md:overflow-y-auto`, e ler
  // "tem a classe" responderia "sim" também abaixo do corte, onde ela não vale.
  const { overflowY } = getComputedStyle(el);
  return overflowY === "auto" || overflowY === "scroll" ? el : null;
}

/** Posição vertical atual, venha ela do frame ou do documento. */
export function getAppScrollTop(): number {
  const el = getAppScroller();
  return el ? el.scrollTop : window.scrollY;
}

/** Move a rolagem para `top` no scroller ativo. */
export function scrollAppTo(top: number, behavior: ScrollBehavior = "instant"): void {
  const el = getAppScroller();
  if (el) el.scrollTo({ top, behavior });
  else window.scrollTo({ top, behavior });
}

/** Atalho do caso dominante — "esta tela abre no começo". */
export function scrollAppToTop(behavior: ScrollBehavior = "instant"): void {
  scrollAppTo(0, behavior);
}

/**
 * Assina a rolagem ativa. O alvo é resolvido na hora da assinatura, então quem
 * usa isto precisa reassinar quando a rota muda (o frame só existe em algumas)
 * — é o que o `ScrollToTop` faz, com `key` na lista de dependências.
 */
export function subscribeAppScroll(onScroll: () => void): () => void {
  const target: HTMLElement | Window = getAppScroller() ?? window;
  target.addEventListener("scroll", onScroll, { passive: true });
  return () => target.removeEventListener("scroll", onScroll);
}

/**
 * Trava e destrava a rolagem de fundo enquanto um modal está aberto.
 *
 * Travar só o `body` deixaria o frame rolando atrás do modal no desktop, que é
 * o sintoma clássico de "o fundo se mexe quando eu rolo dentro do diálogo".
 * Devolve a função que restaura exatamente o valor anterior — e não `""` —
 * para não apagar um lock de um modal que ainda esteja aberto por baixo.
 */
export function lockAppScroll(): () => void {
  const el = getAppScroller();
  const target = el ?? document.body;
  const previous = target.style.overflow;
  target.style.overflow = "hidden";
  return () => {
    target.style.overflow = previous;
  };
}
